import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
  CampaignPerformance,
} from '../../services/facebookAdsService';
import { format } from 'date-fns';

const FacebookAdsBulkOperations: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<FacebookConnection | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignPerformance[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadCampaigns();
    }
  }, [selectedConnection]);

  const loadConnections = async () => {
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
        if (response.data.length > 0 && !selectedConnection) {
          setSelectedConnection(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    if (!selectedConnection) return;

    setLoading(true);
    try {
      const response = await FacebookAdsService.getCampaignPerformance(selectedConnection.account_id.toString(), {});
      if (response.success && response.data) {
        setCampaigns(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPause = async () => {
    if (selectedCampaigns.size === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos uma campanha', severity: 'error' });
      return;
    }

    if (!window.confirm(`Tem certeza que deseja pausar ${selectedCampaigns.size} campanha(s)?`)) return;

    setProcessing(true);
    try {
      const response = await FacebookAdsService.bulkPauseCampaigns(
        Array.from(selectedCampaigns).map(id => id.toString()),
        selectedConnection!.account_id.toString()
      );
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Campanhas pausadas', severity: 'success' });
        setSelectedCampaigns(new Set());
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedCampaigns.size === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos uma campanha', severity: 'error' });
      return;
    }

    if (!window.confirm(`Tem certeza que deseja ativar ${selectedCampaigns.size} campanha(s)?`)) return;

    setProcessing(true);
    try {
      const response = await FacebookAdsService.bulkActivateCampaigns(
        Array.from(selectedCampaigns).map(id => id.toString()),
        selectedConnection!.account_id.toString()
      );
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Campanhas ativadas', severity: 'success' });
        setSelectedCampaigns(new Set());
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCampaigns.size === 0) {
      setSnackbar({ open: true, message: 'Selecione pelo menos uma campanha', severity: 'error' });
      return;
    }

    if (!window.confirm(`⚠️ ATENÇÃO: Tem certeza que deseja DELETAR ${selectedCampaigns.size} campanha(s)? Esta ação não pode ser desfeita!`)) return;

    setProcessing(true);
    try {
      const response = await FacebookAdsService.bulkDeleteCampaigns(
        Array.from(selectedCampaigns).map(id => id.toString()),
        selectedConnection!.account_id.toString()
      );
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Campanhas deletadas', severity: 'success' });
        setSelectedCampaigns(new Set());
        loadCampaigns();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Operações em Lote
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Conta</InputLabel>
            <Select
              value={selectedConnection?.account_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const conn = connections.find((c) => c.account_id === parseInt(value.toString()));
                setSelectedConnection(conn || null);
              }}
              label="Conta"
            >
              {connections.map((conn) => (
                <MenuItem key={conn.account_id} value={conn.account_id}>
                  {conn.account_name || `Conta ${conn.account_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadCampaigns} disabled={loading}>
            Atualizar
          </Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Operações Disponíveis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Selecione as campanhas abaixo e escolha a operação desejada.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={handleBulkPause}
              disabled={selectedCampaigns.size === 0 || processing}
            >
              Pausar ({selectedCampaigns.size})
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayIcon />}
              onClick={handleBulkActivate}
              disabled={selectedCampaigns.size === 0 || processing}
            >
              Ativar ({selectedCampaigns.size})
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              disabled={selectedCampaigns.size === 0 || processing}
            >
              Deletar ({selectedCampaigns.size})
            </Button>
            {selectedCampaigns.size > 0 && (
              <Button
                variant="outlined"
                onClick={() => setSelectedCampaigns(new Set())}
              >
                Limpar Seleção
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {selectedCampaigns.size > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedCampaigns.size} campanha(s) selecionada(s)
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedCampaigns.size > 0 && selectedCampaigns.size < campaigns.length}
                  checked={campaigns.length > 0 && selectedCampaigns.size === campaigns.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCampaigns(new Set(campaigns.map(c => c.campaign_id)));
                    } else {
                      setSelectedCampaigns(new Set());
                    }
                  }}
                />
              </TableCell>
              <TableCell>Campanha</TableCell>
              <TableCell align="right">Status</TableCell>
              <TableCell align="right">Gasto</TableCell>
              <TableCell align="right">ROAS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow key={campaign.campaign_id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedCampaigns.has(campaign.campaign_id)}
                    onChange={(e) => {
                      const newSet = new Set(selectedCampaigns);
                      if (e.target.checked) {
                        newSet.add(campaign.campaign_id);
                      } else {
                        newSet.delete(campaign.campaign_id);
                      }
                      setSelectedCampaigns(newSet);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {campaign.campaign_name}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={campaign.campaign_status}
                    color={campaign.campaign_status === 'ACTIVE' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(campaign.total_spend)}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${campaign.roas.toFixed(2)}x`}
                    color={campaign.roas >= 3 ? 'success' : campaign.roas >= 1.5 ? 'info' : 'warning'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">Nenhuma campanha encontrada</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {processing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacebookAdsBulkOperations;


